
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { ChatGroup, ChatMessage } from '@/types/chat';
import ChatHeader from './ChatHeader';
import MessagesList from './MessagesList';
import ChatInputArea from './ChatInputArea';
import { useIsMobile } from '@/hooks/use-mobile';

interface ChatMessagesAreaProps {
  selectedGroup: ChatGroup;
  messages: any[];
  user: any;
  newMessage: string;
  onMessageChange: (value: string) => void;
  onSendMessage: (e: React.FormEvent) => void;
  canCreateGroup: boolean;
  onCreateGroup: () => void;
  isGroupCreator?: boolean;
  onGroupDeleted?: () => void;
  onUserLeft?: () => void;
  highlightMessageId?: string;
  onJumpToMention?: (messageId: string) => void;
  replyingToMessageId?: string | null;
  onSetReplyingTo?: (messageId: string | null) => void;
}

const ChatMessagesArea = ({
  selectedGroup,
  messages,
  user,
  newMessage,
  onMessageChange,
  onSendMessage,
  canCreateGroup,
  onCreateGroup,
  isGroupCreator = false,
  onGroupDeleted,
  onUserLeft,
  highlightMessageId,
  onJumpToMention,
  replyingToMessageId,
  onSetReplyingTo
}: ChatMessagesAreaProps) => {
  const isMobile = useIsMobile();
  const [replyingToMessage, setReplyingToMessage] = useState<ChatMessage | null>(null);

  // Find the message being replied to
  useEffect(() => {
    if (replyingToMessageId && messages) {
      const message = messages.find(m => m.message_id === replyingToMessageId);
      setReplyingToMessage(message || null);
    } else {
      setReplyingToMessage(null);
    }
  }, [replyingToMessageId, messages]);

  // Handle reply button click from MessagesList
  const handleReplyClick = (messageId: string, message: ChatMessage) => {
    console.log('Setting reply context to message:', messageId);
    onSetReplyingTo?.(messageId);
  };

  // Handle cancel reply
  const handleCancelReply = () => {
    console.log('Canceling reply context');
    onSetReplyingTo?.(null);
  };

  // Debug logging for ChatMessagesArea
  useEffect(() => {
    console.log('=== CHAT MESSAGES AREA RENDER ===');
    console.log('Selected group:', selectedGroup?.chat_name, selectedGroup?.chat_id);
    console.log('Messages count:', messages?.length || 0);
    console.log('Messages data:', messages);
    console.log('User:', user?.username);
    console.log('Is group creator:', isGroupCreator);
    console.log('Is mobile:', isMobile);
  }, [selectedGroup, messages, user, isGroupCreator, isMobile]);

  // Ensure we have a valid selectedGroup before rendering
  if (!selectedGroup) {
    console.log('No selected group - showing placeholder');
    return (
      <div className="flex items-center justify-center h-full bg-gray-800 border border-gray-700 rounded-lg">
        <div className="text-center">
          <p className="text-gray-300">Please select a chat group to start messaging</p>
          {canCreateGroup && (
            <Button
              onClick={onCreateGroup}
              className="mt-4 bg-sports-green hover:bg-sports-green/90 text-black"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Group
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Determine chat permissions properly
  const chatType: 'open' | 'read_only' = 'open';
  const canSendMessage = isGroupCreator || chatType === 'open';

  console.log('ChatMessagesArea render state:', {
    selectedGroup: selectedGroup.chat_name,
    messagesCount: messages?.length || 0,
    user: user?.username,
    canSendMessage,
    chatType,
    isMobile,
    messagesArray: Array.isArray(messages) ? 'valid array' : 'invalid array',
    messagesType: typeof messages
  });

  return (
    <div className="h-full bg-gray-800 border border-gray-700 rounded-lg flex flex-col">
      {/* FIXED HEADER - only show on desktop */}
      {!isMobile && (
        <div className="flex-shrink-0 border-b border-gray-700">
          <ChatHeader 
            selectedGroup={selectedGroup}
            user={user}
            isCreator={isGroupCreator}
            chatType={chatType}
            isMobile={isMobile}
            messages={messages}
            onJumpToMention={onJumpToMention}
          />
        </div>
      )}
      
      {/* SCROLLABLE MESSAGE LIST AREA - fills remaining space */}
      <div className="flex-1 min-h-0">
        <MessagesList 
          messages={messages || []}
          selectedGroup={selectedGroup}
          currentUsername={user?.username}
          currentUserId={user?.id}
          chatType={chatType}
          isCreator={isGroupCreator}
          isMobile={isMobile}
          highlightMessageId={highlightMessageId}
          onReplyClick={handleReplyClick}
        />
      </div>
      
      {/* FIXED INPUT AT BOTTOM - only show on desktop */}
      {!isMobile && (
        <div className="flex-shrink-0 border-t border-gray-700">
          <ChatInputArea
            canSendMessage={canSendMessage}
            newMessage={newMessage}
            onMessageChange={onMessageChange}
            onSendMessage={onSendMessage}
            selectedGroup={selectedGroup}
            isMobile={isMobile}
            replyingTo={replyingToMessage}
            onCancelReply={handleCancelReply}
          />
        </div>
      )}
    </div>
  );
};

export default ChatMessagesArea;
